// Pure pricing helpers + constants extracted from Order.jsx.
// No React, no side-effects — safe to unit test in isolation.

import { Contrast, Palette, Sparkles, Sliders, Ban, RotateCw, Bookmark, Book } from "lucide-react";

export const PAPER_SIZES = ["A4", "A3", "A5"];

export const COLOR_OPTIONS = [
  { value: "bw",     label: "B & W",   icon: Contrast },
  { value: "color",  label: "Color",   icon: Palette },
  { value: "auto",   label: "Auto",    icon: Sparkles },
  { value: "custom", label: "Custom",  icon: Sliders },
];

export const BINDING_OPTIONS = [
  { value: "none",      label: "No Binding", icon: Ban },
  { value: "spiral",    label: "Spiral",     icon: RotateCw },
  { value: "tape",      label: "Tape",       icon: Bookmark },
  { value: "hardcover", label: "Hard cover", icon: Book },
];

// Colour palette per binding × cover position. Spiral/Tape front covers can be
// clear (translucent); back covers and every hardcover face use only solid
// colours. Backend mirrors this in DEFAULT_PRICING["cover_colors"].
export const COVER_PALETTE = {
  spiral:    { top: ["clear", "blue", "red", "green"], back: ["black", "blue", "red", "green"] },
  tape:      { top: ["clear", "blue", "red", "green"], back: ["black", "blue", "red", "green"] },
  hardcover: { top: ["black", "blue", "red", "green"], back: ["black", "blue", "red", "green"] },
};

export const COVER_COLOR_HEX = {
  clear: "#E5E5E5",
  black: "#0A0A0A",
  blue:  "#1E40AF",
  red:   "#DC2626",
  green: "#15803D",
};

export function defaultSpec(file) {
  return {
    file_id: file.id,
    file_name: file.original_name,
    pages: file.pages,
    copies: 1,
    print_all_pages: true,
    // 1-indexed list of pages to physically print. Empty = all pages
    // (interpreted with the print_all_pages boolean above).
    print_pages: [],
    paper_size: "A4",
    color_mode: "bw",
    color_pages: [],
    double_sided: true,
    stapling: false,
    binding: "none",
    binding_cover: null,              // legacy alias — kept in sync with top cover
    binding_top_cover: null,
    binding_back_cover: null,
    binding_gold_engraving: false,
    binding_engraving_source: null,   // "cover_page" | "upload"
    binding_engraving_file_id: null,
    binding_engraving_file_name: null,
    lamination: false,
    batch_copies: false,             // combine all copies into one physical doc
    notes: "",
  };
}

export function bindingTierPrice(tiers, sheets) {
  for (const t of tiers || []) {
    if (sheets >= t.min && sheets <= t.max) return Number(t.price);
  }
  if (tiers && tiers.length) {
    return Number(tiers.reduce((a, b) => (a.max > b.max ? a : b)).price);
  }
  return 0;
}

export function calcLocal(item, pricing) {
  if (!pricing) return 0;
  const size = item.paper_size;
  const color = item.color_mode;
  const totalPages = Math.max(1, item.pages || 1);
  // Effective pages to charge = subset if print_all_pages is off, else all.
  const printSet = item.print_all_pages
    ? null
    : new Set((item.print_pages || []).filter((p) => Number.isInteger(p) && p >= 1 && p <= totalPages));
  const effectivePages = printSet ? printSet.size : totalPages;
  if (!effectivePages) return 0;
  const copies = Math.max(1, item.copies || 1);
  const bwRate = Number(pricing.print?.[size]?.bw ?? 0);
  const colorRate = Number(pricing.print?.[size]?.color ?? 0);
  let base;
  if (color === "auto" || color === "custom") {
    // Intersect colour pages with the print subset — never charge colour rate
    // for a page the customer isn't printing.
    const raw = Array.isArray(item.color_pages) ? item.color_pages : [];
    const scoped = raw.filter((p) =>
      Number.isInteger(p) && p >= 1 && p <= totalPages && (!printSet || printSet.has(p))
    );
    const cColor = new Set(scoped).size;
    const cBw = Math.max(0, effectivePages - cColor);
    base = (cColor * colorRate + cBw * bwRate) * copies;
  } else {
    const perPage = Number(pricing.print?.[size]?.[color] ?? 0);
    base = perPage * effectivePages * copies;
  }
  const sheets = item.double_sided ? Math.ceil(effectivePages / 2) : effectivePages;
  // Batching: combine all copies into a single physical document for
  // finishing. Staple/bind/covers apply once; per-page lamination is
  // unchanged (still one lamination job per page across all copies).
  const batch = !!item.batch_copies;
  const finishMult = batch ? 1 : copies;
  const finishSheets = batch ? sheets * copies : sheets;
  let extras = 0;
  if (item.stapling) extras += Number(pricing.stapling || 0) * finishMult;
  if (item.binding && item.binding !== "none") {
    const tiers = pricing.binding?.[item.binding] || [];
    extras += bindingTierPrice(tiers, finishSheets) * finishMult;
  } else if (item.lamination) {
    extras += Number(pricing.lamination?.[size] || 0) * effectivePages * copies;
  }
  return Math.round(base + extras);
}

// Parse a human range string like "1, 3-5, 10" → sorted unique [1,3,4,5,10].
// Silently drops non-numeric tokens; caller validates against page count.
export function parsePageRanges(input) {
  if (!input) return [];
  const out = new Set();
  for (const chunk of String(input).split(/[,\s]+/)) {
    const c = chunk.trim();
    if (!c) continue;
    const m = c.match(/^(\d+)-(\d+)$/);
    if (m) {
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      if (a && b) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        for (let n = lo; n <= hi; n++) out.add(n);
      }
    } else if (/^\d+$/.test(c)) {
      out.add(parseInt(c, 10));
    }
  }
  return Array.from(out).sort((a, b) => a - b);
}

// Compact a list of pages [1,3,4,5,10] → "1, 3-5, 10".
export function formatPageRanges(pages) {
  if (!pages?.length) return "";
  const sorted = [...pages].sort((a, b) => a - b);
  const groups = [];
  let start = sorted[0], prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) { prev = sorted[i]; continue; }
    groups.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = sorted[i]; prev = sorted[i];
  }
  groups.push(start === prev ? `${start}` : `${start}-${prev}`);
  return groups.join(", ");
}

export function calcTotals(subtotal, tier, pricing) {
  const mult = Number(pricing?.tiers?.[tier]?.multiplier ?? 1.0);
  const tieredSubtotal = Math.round(subtotal * mult);
  const feePct = Number(pricing?.convenience_fee_pct ?? 0);
  const feeMin = Number(pricing?.convenience_fee_min ?? 0);
  const fee = subtotal > 0 ? Math.max(Math.round(tieredSubtotal * feePct), feeMin) : 0;
  const minOrder = Number(pricing?.min_order ?? 0);
  const total = Math.max(tieredSubtotal + fee, minOrder);
  return { tieredSubtotal, fee, total, mult };
}
